'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Loading03Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEntityResolver } from '../../../trace-view/context/EntityResolverContext';
import { extractLoginCredentialId } from '../../extractors/login-extractors';

type AuthenticateToolCardProps = {
  input: unknown;
  output?: unknown;
  status: string;
  error?: string;
  durationMs?: number;
};

export function AuthenticateToolCard({
  input,
  output,
  status,
  error,
  durationMs,
}: AuthenticateToolCardProps) {
  const t = useTranslations('designSystem.chat.authenticate');
  const { resolve } = useEntityResolver();
  const credentialId = extractLoginCredentialId(input);
  const credential = credentialId
    ? resolve('credential', credentialId)
    : undefined;
  const role = credential && 'role' in credential ? credential.role : undefined;
  const username =
    credential && 'username' in credential ? credential.username : undefined;
  const outputRole =
    typeof output === 'string'
      ? output.match(/^Authenticated credential \S+ \(role: (.*?)\)/)?.[1]
      : undefined;
  const identity = [role || outputRole, username].filter(Boolean).join(' ');
  const hasError = status === 'error' || Boolean(error);
  const isRunning = !hasError && status !== 'completed';
  const statusLabel = hasError
    ? t('failed')
    : isRunning
      ? t('running')
      : durationMs === undefined
        ? t('completed')
        : t('completedIn', { seconds: (durationMs / 1000).toFixed(1) });

  return (
    <HStack gap={1.5} py={1} minW={0} color="fg.muted" fontSize="xs">
      <Box flexShrink={0}>{t('title')}</Box>
      {identity ? <Box truncate>{identity}</Box> : null}
      {!isRunning ? (
        <>
          <Box aria-hidden="true">•</Box>
          <Box role="status" flexShrink={0}>
            {statusLabel}
          </Box>
        </>
      ) : null}
      {isRunning ? (
        <Box
          role="status"
          aria-label={statusLabel}
          display="flex"
          flexShrink={0}
          animation="spin 1s linear infinite"
        >
          <Loading03Icon size={12} />
        </Box>
      ) : null}
    </HStack>
  );
}
